import { Select, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState, type CSSProperties } from 'react';
import type {
  DeckSummary,
  DrawSpreadCardInput,
  SpreadPlacedCardView,
  SpreadSessionView,
} from '@creativitas/shared';
import { PlayingCard } from '@/components/domain/PlayingCard';
import { fetchDecks } from '@/features/content/api';
import { TABLE_TEXTURE } from '@/lib/assets';
import { CardPreviewModal } from './CardPreviewModal';
import { PresetBoardSpread } from './PresetBoardSpread';
import { ownerLabel, placedInPosition } from './spread-table';
import classes from './SpreadCanvas.module.css';

type PresetSpreadProps = {
  session: SpreadSessionView;
  busy: boolean;
  onDraw: (input: DrawSpreadCardInput) => void;
  /** Whose cards to mark as «мои» on the shared table (SPR-15). */
  viewerParticipantId?: string;
  /**
   * Почему сейчас тянуть нельзя: очередь другого участника (§бэклог 9).
   *
   * Причина, а не булево: закрытая рубашка без объяснения читается как
   * поломка стола.
   */
  lockedReason?: string | null;
  /**
   * Чей расклад показывает стол.
   *
   * У каждого участника свой расклад: одна и та же позиция заполняется каждым
   * отдельно. Стол показывает раскладку одного человека — иначе в одной рамке
   * оказались бы четыре карты сразу. Участник видит свою; ведущий выбирает,
   * чью смотреть.
   */
  viewedParticipantId?: string;
  /** Гостю колоды выдаёт его собственная ручка — по гостевому токену. */
  fetchAvailableDecks?: () => Promise<{ items: DeckSummary[] }>;
};

export function PresetSpread({
  session,
  busy,
  onDraw,
  viewerParticipantId,
  lockedReason,
  viewedParticipantId,
  fetchAvailableDecks,
}: PresetSpreadProps) {
  const [preview, setPreview] = useState<SpreadPlacedCardView | null>(null);
  const active = session.status === 'active';
  const canDraw = active && !lockedReason;

  /*
    Колода выбирается человеком, когда расклад её не закрепил.

    «Моё место под солнцем» знает свои колоды из буклета. У «Шахмат» их нет:
    «Все колоды могут быть использованы» (Дарья, 6 сентября) — значит, выбор
    остаётся за тем, кто тянет, и менять колоду по ходу расклада он вправе.
  */
  const choosesDeck = session.spread.positions.some((position) => position.deckId === null);
  const [chosenDeckId, setChosenDeckId] = useState<string | null>(null);
  const decksQuery = useQuery({
    queryKey: ['decks', 'spread-choice', fetchAvailableDecks ? session.id : 'member'],
    queryFn: fetchAvailableDecks ?? (() => fetchDecks({ freeformOnly: true })),
    enabled: choosesDeck,
  });
  const deckOptions = useMemo(
    () =>
      (decksQuery.data?.items ?? [])
        .filter((deck) => deck.access.allowed)
        .map((deck) => ({ value: deck.id, label: `${deck.title} · ${deck.cardCount} карт` })),
    [decksQuery.data],
  );
  const chosenDeck = decksQuery.data?.items.find((deck) => deck.id === chosenDeckId) ?? null;

  const handleDraw = (positionId: string) => {
    const position = session.spread.positions.find((item) => item.id === positionId);
    /* Позиция со своей колодой её и тянет; выбор нужен только позициям без неё. */
    onDraw({
      positionId,
      ...(position?.deckId ? {} : { deckId: chosenDeckId ?? undefined }),
      x: 50,
      y: 50,
    });
  };

  /* На завершённом столе тянуть уже нечего — выбор колоды там только путает. */
  const deckPicker = choosesDeck && active ? (
    <Select
      w={280}
      label="Колода для следующей карты"
      description="У этого расклада колода не закреплена — можно менять её по ходу."
      placeholder={decksQuery.isLoading ? 'Загружаем колоды...' : 'Выберите колоду'}
      data={deckOptions}
      value={chosenDeckId}
      onChange={setChosenDeckId}
      disabled={deckOptions.length === 0}
      comboboxProps={{ withinPortal: true }}
    />
  ) : null;

  /* Тянуть можно, когда выбирать колоду не нужно или она уже выбрана. */
  const readyToDraw = canDraw && (!choosesDeck || Boolean(chosenDeckId));

  /*
    Подсказки «нажимайте на рубашки по порядку» здесь больше нет.

    Она висела над столом всю сессию и объясняла то, что стол показывает сам:
    рубашки пронумерованы, и активна только следующая по счёту. Ведущий видит
    её на каждом раскладе, а места она занимала столько, что стол уезжал вниз
    за край экрана — то есть подсказка мешала смотреть ровно на то, о чём
    рассказывала.
  */
  return (
    <Stack gap="md">
      {lockedReason ? (
        <Text size="sm" c="var(--text-muted)" data-testid="spread-turn-lock">
          {lockedReason}
        </Text>
      ) : null}
      {deckPicker}
      {choosesDeck && active && !chosenDeckId ? (
        <Text size="sm" c="var(--text-muted)" data-testid="spread-deck-hint">
          Выберите колоду — и рубашки на столе станут активными.
        </Text>
      ) : null}

      {session.spread.boardImage ? (
        <PresetBoardSpread
          session={session}
          busy={busy}
          canDraw={readyToDraw}
          onDraw={handleDraw}
          onInspect={setPreview}
          fallbackBackImage={chosenDeck?.cardBackImage ?? null}
          viewerParticipantId={viewerParticipantId}
          viewedParticipantId={viewedParticipantId}
        />
      ) : (
        <div className={classes.viewport}>
          <div
            className={`${classes.canvas} ${classes.presetCanvas}`}
            data-testid="preset-canvas"
            style={{ '--spread-table-image': `url(${TABLE_TEXTURE})` } as CSSProperties}
          >
            {session.spread.positions.map((position) => {
              const placed = placedInPosition(session, position.id, viewedParticipantId);
              return (
                <div
                  key={position.id}
                  className={classes.presetPosition}
                  data-testid={`preset-position-${position.positionIndex}`}
                  style={{ left: `${position.x}%`, top: `${position.y}%` }}
                >
                  {/*
                    A drawn card settles into its position — motion here says
                    "this card just arrived", which is the only reason §62 allows
                    animation at all. `key` on the card id makes the entrance run
                    once per draw, not on every re-render.
                  */}
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={placed?.id ?? `empty-${position.id}`}
                      initial={placed ? { opacity: 0, scale: 0.92, y: -10 } : false}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ duration: 0.28, ease: [0.2, 0.7, 0.3, 1] }}
                    >
                      <PlayingCard
                        frontImage={placed?.card.frontImage ?? null}
                        backImage={position.deckBackImage ?? chosenDeck?.cardBackImage ?? null}
                        faceUp={Boolean(placed)}
                        caption={String(position.positionIndex + 1)}
                        label={placed ? position.title : `Вытянуть карту: ${position.title}`}
                        width={132}
                        disabled={busy || (!readyToDraw && !placed)}
                        onClick={
                          placed
                            ? () => setPreview(placed)
                            : readyToDraw
                              ? () => handleDraw(position.id)
                              : undefined
                        }
                      />
                    </motion.div>
                  </AnimatePresence>
                  <Text
                    component="div"
                    className={classes.positionLabel}
                    /*
                      SPR-15: the table stays open — everyone sees every card —
                      but a participant has to be able to find their own among
                      them. The caption says whose card it is, and the viewer's
                      own captions are marked rather than everyone else's hidden.
                    */
                    data-mine={
                      placed && placed.placedByParticipantId === viewerParticipantId
                        ? 'true'
                        : undefined
                    }
                  >
                    {position.title}
                    <Text component="span" display="block" size="xs" c="var(--text-subtle)">
                      {placed
                        ? ownerLabel(session, placed.placedByParticipantId, viewerParticipantId)
                        : position.deckTitle}
                    </Text>
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <CardPreviewModal card={preview} onClose={() => setPreview(null)} />
    </Stack>
  );
}
